import React, { createContext, useState, useContext, useCallback } from 'react';
import { Client, SepaTransaction } from '../types/index';

interface ClientContextType {
    activeClients: Client[];
    deletedClients: Client[];
    transactions: SepaTransaction[];
    updateActiveClients: (clientsOrUpdater: Client[] | ((prevClients: Client[]) => Client[])) => void;
    updateDeletedClients: (clients: Client[]) => void;
    updateTransactions: (transaction: SepaTransaction) => void;
    setTransactions: (transactions: SepaTransaction[]) => void;
    deleteClient: (clientId: string) => void;
    restoreClient: (client: Client) => void; 
    filteredClients: Client[];
    setFilteredClients: React.Dispatch<React.SetStateAction<Client[]>>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [activeClients, setActiveClients] = useState<Client[]>([]);
    const [deletedClients, setDeletedClients] = useState<Client[]>([]);
    const [transactions, setTransactionsState] = useState<SepaTransaction[]>([]);
    const [filteredClients, setFilteredClients] = useState<Client[]>([]);

    const updateActiveClients = useCallback((clientsOrUpdater: Client[] | ((prevClients: Client[]) => Client[])) => {
        setActiveClients(prev => {
            const newClients = typeof clientsOrUpdater === 'function' ? clientsOrUpdater(prev) : clientsOrUpdater;
            // console.log('Updating active clients in context:', newClients);
            setFilteredClients(newClients);
            return newClients;
        });
    }, []);

    const updateDeletedClients = useCallback((clients: Client[]) => {
        setDeletedClients(clients);
    }, []);

    const updateTransactions = useCallback((transaction: SepaTransaction) => {
        setTransactionsState(prev => [...prev, transaction]);
    }, []);

    const setTransactions = useCallback((transactions: SepaTransaction[]) => {
        setTransactionsState(transactions);
    }, []);

    const deleteClient = useCallback((clientId: string) => {
        // setActiveClients(prev => prev.filter(client => client._id !== clientId));
        // console.log('activeClients antes del SetDeletedClients:', activeClients);
        setDeletedClients(prev => {
            const clientToDelete = activeClients.find(client => client._id === clientId);
            console.log('clientToDelete:', clientToDelete);
            if (clientToDelete) {
                const updatedClient = {
                    ...clientToDelete,
                    activo: false,
                    suscripciones: clientToDelete.suscripciones?.map(suscripcion => ({
                        ...suscripcion,
                        fechaBajaContrato: new Date().toISOString(), // Actualiza la fecha de baja
                        pagador_id: suscripcion.pagador_id ? undefined : suscripcion.pagador_id, // Actualiza pagador_id a undefined si está presente
                        tipoPago: suscripcion.pagador_id ? "Domiciliaciones" : suscripcion.tipoPago // Actualiza tipoPago a "Domiciliaciones" si pagador_id está presente 
                    })) || [] // Asegura que suscripciones sea un array vacío si no existe
                };
                return [...prev, updatedClient];
            }
            return prev;
        });
    }, [activeClients]);

    const restoreClient = useCallback((client: Client) => {
        // Eliminar el cliente restaurado de deletedClients
        setDeletedClients(prev => prev.filter(c => c._id !== client._id));
        // Añadir el cliente restaurado a activeClients
        setActiveClients(prev => [...prev, client]);
    }, []);

    return (
        <ClientContext.Provider value={{ 
            activeClients, 
            deletedClients,
            transactions, 
            updateActiveClients, 
            updateDeletedClients,
            updateTransactions,
            setTransactions,
            deleteClient,
            restoreClient,
            filteredClients,
            setFilteredClients,
        }}>
            {children}
        </ClientContext.Provider>
    );
};

export const useClientContext = () => {
    const context = useContext(ClientContext);
    if (context === undefined) {
        throw new Error('useClientContext must be used within a ClientProvider');
    }
    return context;
};